import React, { useContext, useMemo } from 'react';
import { Box, Grid } from '@mui/material';

import RouteWrapper from './RouteWrapper';
import { DataContext } from '../components/data/DataContext';
import { DataContextType } from '../components/data_types/interfaces';
import {
    MIN_PERIOD_ROUNDS,
    WEEKDAYS,
    bestPeriod,
    busiestPeriod,
    summarizeByWeekday,
    toughestPeriod,
} from '../components/data/period_insights';
import { AvgChangeBarChart, ChartLegendNote, InsightCard, PeriodNav, PeriodTile } from '../components/shared/PeriodComponents';
import { DS, Panel } from '../components/shared/Dashboard';

const ByDayOfWeek = () => {
    const context = useContext<DataContextType | undefined>(DataContext);
    const { historyData } = context || {};

    const days = useMemo(() => summarizeByWeekday(historyData ?? []), [historyData]);
    const best = bestPeriod(days);
    const toughest = toughestPeriod(days);
    const busiest = busiestPeriod(days);
    const needMore = `Play ${MIN_PERIOD_ROUNDS}+ rounds on a day to rank it.`;

    return (
        <RouteWrapper>
            <PeriodNav title="Your week" subtitle="Which days you play best, and which days you play most." />

            <InsightCard overline="Best day" accent={DS.gain} period={best} metric="avg" emptyText={needMore} delay={0.05} />
            <InsightCard overline="Toughest day" accent={DS.loss} period={best && toughest?.key !== best.key ? toughest : undefined} metric="avg" emptyText={needMore} delay={0.1} />
            <InsightCard overline="Most played" accent={DS.info} period={busiest} metric="count" emptyText="No rounds yet." delay={0.15} />

            <Panel title="Elo per round by day" subtitle="Average rating change per round played on each weekday" delay={0.1}>
                <ChartLegendNote />
                <AvgChangeBarChart periods={days} labels={WEEKDAYS.map(d => d.short)} />
            </Panel>

            <Panel title="Day by day" subtitle="How your rounds usually go on each day" delay={0.15}>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 1.5 }}>
                    {days.map((day, i) => (
                        <PeriodTile
                            key={day.key}
                            period={day}
                            delay={0.15 + i * 0.03}
                            highlight={day.key === best?.key ? 'best' : day.key === toughest?.key && toughest?.key !== best?.key ? 'toughest' : undefined}
                        />
                    ))}
                </Box>
            </Panel>

            <Grid item xs={12} sx={{ mb: 6 }} />
        </RouteWrapper>
    );
};

export default ByDayOfWeek;
